import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

// ---- Developer subscription override ----
// If NODE_ENV === 'development' OR the user email is on the allowlist,
// hardcode `subscription_status = 'pro_tier'` (an internal high-tier token
// the route guards treat as fully paid). This bypasses Stripe locally so
// engineers land on the dashboard without a real checkout.
const DEV_EMAILS = new Set([
  "developer@receiptsheets.com",
  "gking4001@gmail.com",
]);
// Founder Space-Age Control Deck override — matching these emails also force
// `space_age_deck_enabled = true` on the client so the staging dashboard
// paints the commodity ticker + Weather HUD without needing a profile toggle
// round-trip. Mirrors the same rule the backend `/auth/me` applies.
const FOUNDER_DECK_EMAILS = new Set(["gking4001@gmail.com"]);
const isDevEnv =
  typeof process !== "undefined" && process.env.NODE_ENV === "development";

export function applyDevOverride(user) {
  if (!user) return user;
  const email = (user.email || "").toLowerCase();
  const shouldOverride = isDevEnv || DEV_EMAILS.has(email);
  // Founder Space-Age deck override — always on, regardless of the DB flag.
  const deckOverride = FOUNDER_DECK_EMAILS.has(email)
    ? { space_age_deck_enabled: true }
    : {};
  if (!shouldOverride) return { ...user, ...deckOverride };
  // Never downgrade a genuinely-earned premium_shield tier — the server is the
  // source of truth for elite subscribers.  Only elevate lower tiers.
  const serverTier = String(user.subscription_tier || "").toLowerCase();
  if (serverTier === "premium_shield") {
    return { ...user, ...deckOverride, __dev_override: false };
  }
  return {
    ...user,
    ...deckOverride,
    // Local preview override — force Business role so every gated widget
    // (Amazon / Walmart / DoorDash sync, Smart Visa, Team Directory, etc.)
    // renders in its fully unlocked state without a Stripe round-trip.
    subscription_status: "active_business",
    subscriptionStatus: "active_business",
    subscription_tier: "business",
    __dev_override: true,
  };
}

export function AuthProvider({ children }) {
  const [rawUser, setRawUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Public setter still transparently applies the dev override so every
  // downstream consumer of `user` sees the boosted status.
  const setUser = (u) => setRawUser(u ? applyDevOverride(u) : u);
  const user = rawUser; // rawUser has already been passed through applyDevOverride

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setRawUser(applyDevOverride(data));
    } catch {
      setRawUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Bind ::selection palette to subscription tier — cockpit cyan for Pro,
  // premium purple for Business. Applied via <body data-tier="..."> which
  // index.css targets. Kept as a side effect so no page structure changes.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const tier = user?.subscription_tier || user?.subscriptionTier || "";
    if (tier) document.body.setAttribute("data-tier", String(tier).toLowerCase());
    else document.body.removeAttribute("data-tier");
  }, [user]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* noop */
    }
    setRawUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, setLoading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
